import { useMemo } from 'react'

interface UserRole {
    id: number
    roleId: number
    organizationId: string | null
    role: { id: number; key: string }
}

function getUserFromStorage(): { userRoles?: UserRole[] } | null {
    const raw = sessionStorage.getItem('user') || localStorage.getItem('user')
    if (!raw) return null
    try {
        return JSON.parse(raw)
    } catch {
        return null
    }
}

const CMS_ROLE_KEYS = ['cms-admin', 'cms-editor']

/** Distinct organization IDs this user holds a CMS role (cms-admin/cms-editor) in. */
export function useCmsOrgIds(): string[] {
    return useMemo(() => {
        const user = getUserFromStorage()
        if (!user?.userRoles) return []
        const ids = new Set<string>()
        for (const r of user.userRoles) {
            if (CMS_ROLE_KEYS.includes(r.role.key) && r.organizationId) {
                ids.add(r.organizationId)
            }
        }
        return Array.from(ids)
    }, [])
}

export function useCmsOrganizationId(): string | null {
    const cmsOrgIds = useCmsOrgIds()
    return useMemo(() => {
        // Multiple CMS orgs — user must pick via selector
        if (cmsOrgIds.length > 1) return null
        if (cmsOrgIds.length === 1) return cmsOrgIds[0]

        // Fallback: non-CMS organization-admin role
        const user = getUserFromStorage()
        const orgAdmin = user?.userRoles?.find(
            (r) => r.role.key === 'organization-admin'
        )
        if (orgAdmin?.organizationId) return orgAdmin.organizationId

        // Super-admin returns null — orgId comes from context selector
        return null
    }, [cmsOrgIds])
}

/** Returns true when the current user is a super-admin who needs to pick an org. */
export function useIsCmsSuperAdmin(): boolean {
    return useMemo(() => {
        const user = getUserFromStorage()
        if (!user?.userRoles) return false
        return user.userRoles.some((r) => r.role.key === 'super-admin')
    }, [])
}
